import { datadogRum } from '@datadog/browser-rum';
import { config } from '@iheartjane/config';
import {
  useCart,
  useDeleteCart as useV3DeleteCart,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useQueryClient } from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';

import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  updateLocalCartUuid,
  useDeleteCart,
  useGetCart,
  useLocalCartUuid,
} from '@jane/kiosk/data-access';
import { useTypedRouteLoaderData } from '@jane/kiosk/hooks/lib/useTypedRouteLoaderData';
import { useModalsDisplayedContext } from '@jane/kiosk/providers/lib/ModalsDisplayedProvider';
import { useProductTracking } from '@jane/kiosk/providers/lib/ProductTrackingProvider';
import { useSearchContext } from '@jane/kiosk/providers/lib/SearchProvider';
import { useSessionContext } from '@jane/kiosk/providers/lib/SessionProvider';
import { useGetServerVersion } from '@jane/shared/data-access';

interface ResetKioskProps {
  shouldDeleteCart?: boolean;
  shouldNavigateToMenu?: boolean;
}

export const useResetKiosk = () => {
  const { storeId } = useTypedRouteLoaderData<RootLoaderType>('root');
  const { resetSearchState } = useSearchContext();
  const { setCommsBannerDisplayed } = useModalsDisplayedContext();
  const {
    inlineAds: { resetInlineAdIds },
    cartToppers: { resetCartTopperIds },
  } = useProductTracking();

  const { resetSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { data: server } = useGetServerVersion();

  /**
   * Adding searchParams to the query is a way to tell the browser
   * to run a hard refresh of the page. See the service worker in '/kiosk/src/main'
   * for landing page implementation.
   * https://stackoverflow.com/questions/2099201/javascript-hard-refresh-of-current-page
   */
  const clientReload = useCallback(
    (shouldNavigateToMenu = true) => {
      if (shouldNavigateToMenu) {
        // send error to datadog if the client is not running the most up to date version
        if (config.buildVersion !== server?.buildVersion) {
          datadogRum.addError('Client reload', {
            type: 'KIOSK_RESET',
            versionFrom: config.buildVersion,
            versionTo: server?.buildVersion,
          });
        }
        // complete the reload
        window.location.assign(`stores/${storeId}?v=${nanoid()}`);
      }
    },
    [storeId, server?.buildVersion]
  );

  const onDeleteCartSuccess = useCallback(() => {
    resetInlineAdIds();
    resetCartTopperIds();
    resetSearchState();
    setCommsBannerDisplayed(false);
    resetSession();
  }, [
    resetInlineAdIds,
    resetCartTopperIds,
    resetSearchState,
    setCommsBannerDisplayed,
    resetSession,
  ]);

  const { mutate: deleteCart, isPending } = useDeleteCart({
    onSuccess: onDeleteCartSuccess,
  });
  const { mutate: v3DeleteCart, isPending: v3IsLoading } = useV3DeleteCart({
    onSuccess: onDeleteCartSuccess,
  });
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const cartUuid = useLocalCartUuid();
  const { data: v3Cart } = useCart(
    { appMode: 'kiosk', uuid: cartUuid ?? '' },
    { enabled: !!useV3Cart && !!cartUuid }
  );
  const { data: v1Cart } = useGetCart(!useV3Cart);
  const cart = useV3Cart ? v3Cart : v1Cart;

  const resetKiosk = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (
      { shouldDeleteCart, shouldNavigateToMenu }: ResetKioskProps = {
        shouldDeleteCart: true,
        shouldNavigateToMenu: true,
      }
    ) => {
      if (!cart?.uuid) {
        onDeleteCartSuccess();
      }
      if (cart?.uuid && shouldDeleteCart) {
        if (useV3Cart) {
          v3DeleteCart({ appMode: 'kiosk', uuid: cart.uuid });
        } else {
          deleteCart({ uuid: cart.uuid });
        }
        // Clear the persisted uuid synchronously so the post-reset reload boots
        // without it — useCart's `enabled: !!cartUuid` gate then prevents a fetch
        // of the just-deleted cart. v1 relied on the useGetCart empty-cart effect
        // for this self-healing, which is disabled in v3 mode.
        updateLocalCartUuid(null);
      } else {
        updateLocalCartUuid(null);
        queryClient.invalidateQueries({ queryKey: ['cart'] });
        onDeleteCartSuccess();
      }
      janeMixpanel.reset();
      clientReload(shouldNavigateToMenu);
    },
    [
      cart?.uuid,
      useV3Cart,
      v3DeleteCart,
      deleteCart,
      onDeleteCartSuccess,
      queryClient,
      clientReload,
    ]
  );
  return {
    isResetting: useV3Cart ? v3IsLoading : isPending,
    resetKiosk,
  };
};
